import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { PortableText } from '@portabletext/react';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import RelatedPostsCarousel from '../../components/RelatedPostsCarousel';
import ShareButtons from '../../components/ShareButtons';
import MainNavigation from '../../components/layout/MainNavigation';
import './blog.css';

import { BASE_URL, BUSINESS_ID, PERSON_ID } from '../../components/SEO';

const PostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { posts: blogPosts, loading } = useBlogPosts();

  const post = blogPosts.find(post => post.slug === slug);

  useEffect(() => {
    if (!loading && !post) {
      navigate('/blog');
    }
  }, [post, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MainNavigation />
        <div className="pt-16" />
        <div className="container mx-auto px-4 py-20 max-w-3xl animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto" />
          <div className="h-64 bg-gray-200 rounded" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const postUrl = `${BASE_URL}/blog/${post.slug}`;
  const postTitle = `${post.title} | eFortiPsi - Psicóloga Eliane Forti Fernandes`;
  const postImage = post.coverImage?.startsWith('http') ? post.coverImage : `${BASE_URL}/og-image.jpg`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: postImage,
    author: {
      '@type': 'Person',
      '@id': PERSON_ID,
      name: post.author,
      url: BASE_URL,
    },
    publisher: {
      '@type': 'Organization',
      '@id': BUSINESS_ID,
      name: 'eFortiPsi',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/favicon.png`,
      },
    },
    datePublished: post.date,
    dateModified: post.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
    keywords: post.tags.join(', '),
    articleSection: 'Psicologia e Saúde Mental',
    inLanguage: 'pt-BR',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${BASE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: postUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <MainNavigation />
      <Helmet>
        <title>{postTitle}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags.join(', ')} />
        <meta name="author" content={post.author} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={postUrl} />

        {/* Open Graph - Article */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={postImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={post.title} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:site_name" content="eFortiPsi - Psicóloga Eliane Forti Fernandes" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content="Psicologia e Saúde Mental" />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={postImage} />
        <meta name="twitter:image:alt" content={post.title} />

        {/* Schema.org - Article */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

        {/* Schema.org - BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Espaçamento para evitar sobreposição com o menu fixo */}
      <div className="pt-16"></div>

      {/* Post Header */}
      <header className="bg-gradient-to-r from-[#2a3c5c] to-[#4a6c9c] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
            <div className="flex items-center justify-center text-sm mb-4">
              <span>{new Date(post.date).toLocaleDateString('pt-BR')}</span>
              <span className="mx-2">•</span>
              <span>{post.author}</span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-white bg-opacity-20 text-white text-xs px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </header>

      {/* Post Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-auto"
                loading="eager"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
              <div className="prose prose-lg max-w-none">
                {Array.isArray(post.content) ? (
                  <PortableText value={post.content as Parameters<typeof PortableText>[0]['value']} />
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {post.content as string}
                  </ReactMarkdown>
                )}
              </div>

              {/* Botões de compartilhamento */}
              <div className="mt-8 py-6 border-t border-gray-200">
                <ShareButtons
                  url={postUrl}
                  title={post.title}
                />
              </div>
            </div>

            {/* Posts relacionados */}
            <div className="mt-8">
              <RelatedPostsCarousel
                currentPostId={parseInt(post.id)}
                posts={blogPosts}
              />
            </div>

            {/* Navegação entre posts */}
            <div className="mt-12 border-t border-gray-200 pt-8">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                  {blogPosts.findIndex(p => p.id === post.id) > 0 ? (
                    <Link
                      to={`/blog/${blogPosts[blogPosts.findIndex(p => p.id === post.id) - 1].slug}`}
                      className="flex items-center text-[#2a3c5c] hover:text-[#4a6c9c] transition-colors duration-200"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2 flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Post anterior</span>
                    </Link>
                  ) : <div className="h-8"></div>}
                </div>

                <div className="col-span-1 text-right">
                  {blogPosts.findIndex(p => p.id === post.id) < blogPosts.length - 1 ? (
                    <Link
                      to={`/blog/${blogPosts[blogPosts.findIndex(p => p.id === post.id) + 1].slug}`}
                      className="flex items-center justify-end text-[#2a3c5c] hover:text-[#4a6c9c] transition-colors duration-200"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <span>Próximo post</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-2 flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  ) : <div className="h-8"></div>}
                </div>
              </div>
            </div>
          </motion.div>
        </article>
      </main>

      {/* Back to Blog Link */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <Link
          to="/blog"
          className="inline-flex items-center text-[#2a3c5c] hover:text-[#4a6c9c] transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Voltar para o blog
        </Link>
      </div>
    </div>
  );
};

export default PostDetail;
